import {
  BundlesEnum,
  FieldTypeEnum,
  FormReactFCEnum,
  ParagraphEnum,
  ParagraphTypeEnum,
} from './constants';
import { ContentState, convertToRaw } from 'draft-js';
import Jsona from 'jsona';
import { backHomeLink } from 'utils/constants';
import htmlToDraft from 'html-to-draftjs';
import parse from 'html-react-parser';

const dataFormatter = new Jsona();

const parseEntity = (entity) => ({
  field_type: entity.attributes.field_type,
  label: entity.attributes.label,
  description: entity.attributes?.description
    ? parse(entity.attributes?.description)
    : '',
  required: entity.attributes.required,
  ...((entity?.attributes?.field_type === FieldTypeEnum.IMAGE ||
    entity?.attributes?.field_type === FieldTypeEnum.FILE) && {
    imgTranslatable:
      entity?.attributes?.third_party_settings?.content_translation
        ?.translation_sync?.file === 'file',
    altTranslatable:
      entity?.attributes?.third_party_settings?.content_translation
        ?.translation_sync?.file === 'file' ||
      entity?.attributes?.third_party_settings?.content_translation
        ?.translation_sync?.alt === 'alt',
  }),
  translatable: entity.attributes.translatable, // i campi non traducibili non appaiono nella form in inglese e non vengono aggiunti alle chiamate di salvataggio\modifica in inglese
  default_value: entity.attributes.default_value,
  settings: entity.attributes.settings, //i settings contengono informazioni extra necessarie per la costruzione del campo. In questo caso ad es. l’impostazione specifica che il campo contiene entità di tipo taxonomy term appartenenti al vocabolario espositori stanza.
  field_name: entity.attributes.field_name, //utile per collegarlo al rispettivo form
  bundle: entity.attributes.bundle || '',
  format: entity.attributes?.settings?.allowed_formats?.[0] || undefined,
  id: entity.id,
});

const parseEntityWithForm = ({ entities, forms }) =>
  entities.map((entity) => {
    const parsedEntity = parseEntity(entity);
    const formFound =
      Object.entries(forms.attributes.content).find(
        ([key, value]) => key === parsedEntity.field_name && value
      )?.[1] || undefined;
    return { ...parsedEntity, form: formFound || {} };
  });

const parseEntityWithReactFC = ({ entities, forms }) => {
  const formsToHide = Object.entries(forms.attributes.hidden)
    // eslint-disable-next-line unused-imports/no-unused-vars
    .filter(([_, v]) => !!v)
    // eslint-disable-next-line unused-imports/no-unused-vars
    .map(([k, _]) => k);
  return parseEntityWithForm({ entities, forms })
    .filter((entity) => !formsToHide.find((e) => e === entity.field_name))
    .map((entity) => {
      const type = entity.field_type;
      const fEnum = FieldTypeEnum;
      const rcEnum = FormReactFCEnum;

      const basicInfo = {
        name: entity.field_name,
        label: entity.label,
        description: entity.description,
        isRequired: entity.required,
        isTranslatable: entity.translatable,
        defaultValue: entity.default_value,
        weight: entity.form.weight,
        formType: entity.form.type,
        bundle: entity.bundle || '',
        id: entity.id || '',
        field_type: entity.field_type,
        format: entity.format,
        ...(entity.bundle &&
          entity.bundle !== BundlesEnum.BRAND &&
          entity.bundle !== BundlesEnum.PRODUCT && {
            includesString: entity.settings?.handler ? entity.field_name : '',
          }),
      };

      switch (type) {
        case fEnum.ENTITY_REFERENCE_REVISIONS:
          // Caso Particolare: è il padre di tutti i paraghraph dinamici di Additional Content: al suo interno avrà soltanto le reference per ogni singolo componente aggiuntivo (vimeo_video, youtube_video, technical_sheet..) che vanno chiamati singolarmente per strutturli fedelmente
          return {
            formComponent: rcEnum.ACCORDION,
            formEntity: entity.settings.handler,
            formBundle: entity.settings.handler_settings.target_bundles,
            ...basicInfo,
          };
        case fEnum.STRING_LONG:
        case fEnum.STRING:
        case fEnum.LINK:
        case fEnum.EMAIL:
        case fEnum.YEAR_ONLY:
        case fEnum.INTEGER:
          return {
            formComponent:
              type === fEnum.STRING_LONG ? rcEnum.TEXTAREA : rcEnum.TEXTFIELD,
            ...entity.settings,
            ...entity.form?.settings,
            ...basicInfo,
          };
        case fEnum.TEXT_WITH_SUMMARY:
        case fEnum.TEXT_LONG:
          return {
            formComponent: rcEnum.RICH_TEXT,
            ...basicInfo,
            ...entity.settings,
          };
        case fEnum.ENTITY_REFERENCE:
          return {
            formComponent: rcEnum.AUTOCOMPLETE,
            formEntityType: entity.settings?.handler
              ? entity.settings.handler.split(':')[1]
              : '',
            formEntityBundle: entity.settings?.handler_settings?.target_bundles
              ? Object.values(
                  entity.settings?.handler_settings?.target_bundles
                )[0]
              : '',
            formBundle: entity.settings.handler_settings,
            ...basicInfo,
          };
        case fEnum.BOOLEAN:
          return {
            formComponent: rcEnum.RADIO_GROUP,
            options: [
              { label: entity.settings.off_label, value: '0' },
              { label: entity.settings.on_label, value: '1' },
            ],
            ...basicInfo,
          };
        case fEnum.IMAGE:
          return {
            formComponent: rcEnum.IMAGE_DROPZONE,
            hasAltText: entity.settings.alt_field,
            isAltTextRequired: entity.settings.alt_field_required,
            deafultImage: entity.settings.default_image,
            directory: entity.settings.file_directory,
            extensions: entity.settings.file_extensions,
            maxSize: entity.settings.max_filesize,
            maxResolution: entity.settings.max_resolution,
            minResolution: entity.settings.min_resolution,
            hasTitle: entity.settings.title_field,
            issTitleRequired: entity.settings.title_field_required,
            isImgTranslatable: entity?.imgTranslatable || false,
            isAltTranslatable: entity?.altTranslatable || false,
            ...basicInfo,
          };
        default:
          return entity;
      }
    });
};

const parseHtmlToDraw = (htmlValue) => {
  const contentBlock = htmlToDraft(htmlValue);
  if (contentBlock) {
    const contentState = ContentState.createFromBlockArray(
      contentBlock.contentBlocks,
      contentBlock.entityMap
    );
    const rawContent = convertToRaw(contentState);
    return rawContent;
  }
};

const createFileFromDrupalEntity = async (entity, position = false) => {
  const fileUrl = entity.uri?.url;
  const url = `${backHomeLink}${fileUrl}`;
  const response = await fetch(url);
  const data = await response.blob();
  const metadata = {
    type: entity.filemime,
  };
  return typeof position === 'string'
    ? { img: new File([data], entity.filename, metadata), position: position }
    : new File([data], entity.filename, metadata);
};

const parseGeneralAndBasicInfo = async (dE, components) => {
  const imagesPromises = [];
  const entity = {};
  components.map((c) => {
    const rcEnum = FormReactFCEnum;
    const component = c.formComponent;
    switch (component) {
      case rcEnum.RADIO_GROUP:
        entity[c.name] = dE?.[c.name] ? '1' : '0';
        break;
      case rcEnum.RICH_TEXT:
        dE?.[c.name]?.value
          ? (entity[c.name] = JSON.stringify(
              parseHtmlToDraw(dE?.[c.name]?.value)
            ))
          : null;
        break;
      case rcEnum.AUTOCOMPLETE:
        entity[c.name] = dE?.[c.name]
          ? dE?.[c.name].type === 'node--scheda_espositore'
            ? dE?.[c.name].id
            : dE?.[c.name]?.length
            ? dE[c.name].map((v) => ({
                label: v.name,
                value: v.drupal_internal__tid,
                entityId: v.id,
              }))
            : {
                label: dE[c.name].name,
                value: dE[c.name].drupal_internal__tid,
                entityId: dE[c.name].id,
              }
          : [];
        break;
      case rcEnum.IMAGE_DROPZONE:
        dE?.[c.name]
          ? imagesPromises.push(createFileFromDrupalEntity(dE[c.name]))
          : null;
        break;
      case rcEnum.ACCORDION:
        dE?.[c.name] && dE?.[c.name].length
          ? dE?.[c.name].map(
              (img) =>
                img.bp_image_field &&
                imagesPromises.push(
                  createFileFromDrupalEntity(img.bp_image_field)
                )
            )
          : null;
        break;
      default:
        dE?.[c.name] ? (entity[c.name] = dE[c.name]?.uri || dE[c.name]) : null;
        break;
    }
  });

  if (imagesPromises.length) {
    const images = await Promise.all(imagesPromises);
    images.length &&
      images.map((img) => {
        const foundEntity = Object.entries(dE).find(([, v]) => {
          return (
            v?.filename === img.name &&
            v?.filesize === img.size &&
            v?.filemime === img.type
          );
        });
        if (foundEntity?.length) {
          Object.assign(img, {
            preview: URL.createObjectURL(img),
          });
          entity[foundEntity[0]] = [img];
          entity[`alt_${foundEntity[0]}_0`] =
            foundEntity[1]?.resourceIdObjMeta?.alt ||
            foundEntity[1]?.filename ||
            '';
          entity[`${foundEntity[0]}_id`] = foundEntity[1]?.id;
        }
      });
  }
  return { ...entity, field_save_count: dE?.field_save_count || '0' };
};

const parseAdditionalContents = async (dE) => {
  const preEntity = { additionalContents: [] };
  const entityWithImg = { additionalContents: [] };
  const imagesPromises = [];
  const imagesInfo = [];
  const imagesPromisesSlider = [];
  const imagesInfoSlider = [];
  const getCommonProps = (c, idx) => ({
    name: c?.type?.split('--')?.pop() || '',
    position: idx,
    id: c.id || '',
    entityId: c.id || '',
    revisionId: c.resourceIdObjMeta?.target_revision_id || '',
  });
  dE.map((c, idx) => {
    if (c.type === ParagraphTypeEnum.SD_BRAND_MULTITEXT) {
      c.field_paragraph_simple_text &&
        preEntity.additionalContents.push({
          field_paragraph_simple_text: JSON.stringify(
            parseHtmlToDraw(c.field_paragraph_simple_text?.value)
          ),
          ...getCommonProps(c, idx),
        });
    } else if (c.type === ParagraphTypeEnum.VIMEO_VIDEO) {
      c.field_vimeo_link?.uri &&
        preEntity.additionalContents.push({
          field_vimeo_link: c.field_vimeo_link.uri,
          ...getCommonProps(c, idx),
        });
    } else if (c.type === ParagraphTypeEnum.YOUTUBE_VIDEO) {
      (c.field_url?.uri || c.field_didascalia?.value) &&
        preEntity.additionalContents.push({
          ...(c.field_url?.uri && {
            field_url: c.field_url?.uri,
          }),
          ...(c.field_didascalia?.value && {
            field_didascalia: JSON.stringify(
              parseHtmlToDraw(c.field_didascalia?.value)
            ),
          }),
          ...getCommonProps(c, idx),
        });
    } else if (c.type === ParagraphTypeEnum.SDM_QUOTED_TEXT) {
      c.field_testo_semplice &&
        preEntity.additionalContents.push({
          field_section_title: c.field_testo_semplice,
          ...getCommonProps(c, idx),
        });
    } else if (c.type === ParagraphTypeEnum.TITLE_SECTION) {
      c.field_section_title &&
        preEntity.additionalContents.push({
          field_section_title: c.field_section_title,
          ...getCommonProps(c, idx),
        });
    } else if (c.type === ParagraphTypeEnum.VIRTUAL_SHOWROOM) {
      const link = c.field_link_vs?.uri;
      const title = c.field_titolo_vs;
      if (link || title) {
        preEntity.additionalContents.push({
          ...getCommonProps(c, idx),
          ...(link && {
            field_link_vs: link,
          }),
          ...(title && {
            field_titolo_vs: title,
          }),
        });
      }
      if (c.field_immagine_vs?.uri?.url) {
        imagesInfo.push({
          alt_field_immagine_vs_0:
            c.field_immagine_vs?.resourceIdObjMeta?.alt || '',
          field_immagine_vs: [],
          oldImgId: c.field_immagine_vs?.id,
          position: idx,
        });

        imagesPromises.push(createFileFromDrupalEntity(c.field_immagine_vs));
      }
    } else if (c.type === ParagraphTypeEnum.FREE_IMAGE) {
      const caption = c.field_caption?.value;
      if (c.field_immagine?.uri?.url) {
        imagesInfo.push({
          ...(caption && {
            field_caption: JSON.stringify(parseHtmlToDraw(caption)),
          }),
          alt_field_immagine_0: c.field_immagine?.resourceIdObjMeta?.alt || '',
          ...getCommonProps(c, idx),
          field_immagine: [],
          oldImgId: c.field_immagine?.id,
        });

        imagesPromises.push(createFileFromDrupalEntity(c.field_immagine));
      }
    } else if (c.type === ParagraphTypeEnum.FREE_IMAGE_FULL_SCREEN) {
      if (c.field_immagine_libera_full?.uri?.url) {
        imagesInfo.push({
          alt_field_immagine_libera_full_0:
            c.field_immagine_libera_full?.resourceIdObjMeta?.alt || '',
          ...getCommonProps(c, idx),
          field_immagine_libera_full: [],
          oldImgId: c.field_immagine_libera_full?.id,
        });

        imagesPromises.push(
          createFileFromDrupalEntity(c.field_immagine_libera_full)
        );
      }
    } else if (c.type === ParagraphTypeEnum.DESIGNER) {
      const name = c.field_desiger_name;
      if (name) {
        preEntity.additionalContents.push({
          ...getCommonProps(c, idx),
          field_desiger_name: name,
        });
      }
      if (c.field_image?.uri?.url) {
        imagesInfo.push({
          alt_field_image_0: c.field_image?.resourceIdObjMeta?.alt || '',
          field_image: [],
          oldImgId: c.field_image?.id,
          position: idx,
        });

        imagesPromises.push(createFileFromDrupalEntity(c.field_image));
      }
    } else if (c.type === ParagraphTypeEnum.SLIDER_DETAIL) {
      const slider = c?.field_slider_detail || [];
      if (slider.length) {
        const input = {
          ...getCommonProps(c, idx),
          bp_image_field: [],
          fileAlts: [],
          fileCaptions: [],
          filePositions: c.field_slider_detail?.map((_, idx) => idx),
          oldImgId:
            c.field_slider_detail?.map((img) => img.bp_image_field?.id) || [],
          oldParagraphId: c.field_slider_detail?.map((s) => s.id) || [],
          oldRevisionId:
            c.field_slider_detail?.map(
              (s) => s.drupal_internal__revision_id || ''
            ) || [],
        };
        preEntity.additionalContents.push(input);
        slider.map((s, index) => {
          const v = s.bp_image_field;
          const alt = v.resourceIdObjMeta?.alt || '';
          const caption = s.field_caption?.value
            ? JSON.stringify(parseHtmlToDraw(s.field_caption?.value))
            : '';
          imagesInfoSlider.push({ alt, caption, position: `${idx}-${index}` });
          v?.uri?.url &&
            imagesPromisesSlider.push(
              createFileFromDrupalEntity(v, `${idx}-${index}`)
            );
        });
      }
    } else if (c.type === ParagraphTypeEnum.TECHNICAL_SHEET) {
      if (c.field_sheet_image?.uri?.url) {
        imagesInfo.push({
          alt_field_sheet_image_0:
            c.field_sheet_image?.resourceIdObjMeta?.alt || '',
          field_sheet_image: [],
          oldImgId: c.field_sheet_image?.id,
          position: idx,
        });

        imagesPromises.push(createFileFromDrupalEntity(c.field_sheet_image));
      }
      preEntity.additionalContents.push({
        ...getCommonProps(c, idx),
        ...(c.field_descrizione && {
          field_descrizione: c.field_descrizione,
        }),
        ...(c.field_anno && {
          field_anno: { value: c.field_anno, label: `${c.field_anno}` },
        }),
        ...(c.field_designer?.length && {
          field_designer: {
            label: c.field_designer[0]?.name || '',
            value: c.field_designer[0]?.drupal_internal__tid,
            entityId: c.field_designer[0]?.id,
          },
        }),
        ...(c.field_caratteristiche?.length && {
          field_caratteristiche: c.field_caratteristiche.map((ca) => ({
            label: ca.name || '',
            value: ca.id || '',
          })),
        }),
        ...(c.field_materiali?.length && {
          field_materiali: c.field_materiali.map((ma) => ({
            label: ma.name || '',
            value: ma.id || '',
          })),
        }),
        ...(c.field_processo_produttivo && {
          field_processo_produttivo: c.field_processo_produttivo,
        }),
        ...(c.field_dimensioni && {
          field_dimensioni: c.field_dimensioni,
        }),
        ...(c.field_website?.uri && {
          field_website: c.field_website.uri,
          alt_field_website_0: c.field_website?.title || c.field_website.uri,
        }),
        ...(c.field_modello_3d?.uri && {
          field_modello_3d: c.field_modello_3d.uri,
          alt_field_modello_3d_0:
            c.field_modello_3d?.title || c.field_modello_3d.uri,
        }),
        ...(c.field_collezione?.uri && {
          field_collezione: c.field_collezione.uri,
          alt_field_collezione_0:
            c.field_collezione?.title || c.field_collezione.uri,
        }),
      });
    }
  });
  if (imagesPromises.length) {
    const images = await Promise.all(imagesPromises);
    images.length &&
      images.map((img, idx) => {
        const foundEntity = imagesInfo[idx];
        if (foundEntity) {
          const partiallySaved =
            preEntity.additionalContents.find(
              (ac) => ac.position === foundEntity.position
            ) || {};
          Object.assign(img, {
            preview: URL.createObjectURL(img),
          });
          const key = Object.entries(foundEntity).find(
            ([, v]) => Array.isArray(v) && v.length === 0
          )?.[0];
          key &&
            entityWithImg.additionalContents.push({
              ...foundEntity,
              ...partiallySaved,
              [key]: [img],
            });
        }
      });
  }

  /**
   * Slider
   */
  if (imagesPromisesSlider.length) {
    const images = await Promise.all(imagesPromisesSlider);
    images.length &&
      images.map(({ img, position }) => {
        const foundEntity = imagesInfoSlider.find(
          (e) => e.position === position
        );
        if (foundEntity) {
          Object.assign(img, {
            preview: URL.createObjectURL(img),
          });

          const effectiveSliderPosition = position?.split('-')?.[0];

          const indexAlreadySaved = effectiveSliderPosition
            ? preEntity.additionalContents.findIndex(
                (e) =>
                  e.name === ParagraphEnum.SLIDER_DETAIL &&
                  e.position === +effectiveSliderPosition
              )
            : null;

          if (indexAlreadySaved >= 0) {
            preEntity.additionalContents[
              indexAlreadySaved
            ].bp_image_field?.push(img);
            preEntity.additionalContents[indexAlreadySaved].fileAlts.push(
              foundEntity.alt
            );
            preEntity.additionalContents[indexAlreadySaved].fileCaptions.push(
              foundEntity.caption
            );
          }
        }
      });
  }

  const entity = {
    additionalContents: [
      ...entityWithImg.additionalContents,
      ...preEntity.additionalContents.filter(
        (e) =>
          !entityWithImg.additionalContents.find(
            (img) => img.position === e.position
          )
      ),
    ],
  };
  return entity;
};

const parseFetchedEntity = async ({ entityResponse, components = [] }) => {
  const dE = dataFormatter.deserialize({
    data: entityResponse.data,
    included: entityResponse.included,
  });
  const entity = await parseGeneralAndBasicInfo(dE, components);
  const isProduct = dE.type?.split('--')?.pop() === BundlesEnum.PRODUCT;
  const fCKey = isProduct ? 'field_content' : 'field_contents';

  const additionalEntity = dE?.[fCKey]?.length
    ? await parseAdditionalContents(dE?.[fCKey])
    : null;

  return { entity, additionalEntity };
};

const handleDropAfterErrors = ({
  files,
  maxResolution,
  fileExtension,
  maxSize,
  fieldName,
  i18nErrors,
  setValue,
  setCustomErrors,
}) => {
  var _URL = window.URL || window.webkitURL;
  const file = files.find((f) => f);
  const img = new Image();
  var objectUrl = _URL.createObjectURL(file);
  img.onload = function () {
    const fileWidth = img.width;
    const fileHeight = img.height;
    const fileMaxSize = maxResolution.split('x');
    const maxWidth = parseInt(fileMaxSize?.[0]);
    const maxHeight = parseInt(fileMaxSize?.[1]);
    if (maxWidth !== fileWidth || maxHeight !== fileHeight) {
      return setCustomErrors({
        i18n: {
          title: i18nErrors.width.title,
          body: `${i18nErrors.width.body} ${maxWidth}x${maxHeight}px`,
        },
      });
    }
    const fileFormat = file.path.split('.').slice(-1)[0];
    if (!fileExtension.includes(fileFormat))
      return setCustomErrors({
        i18n: {
          title: i18nErrors.format.title,
          body: `${i18nErrors.format.body} .${fileExtension}`,
        },
      });
    const fileSize = file.size;
    const maxSizeLitteral = maxSize;
    const maxSizeInByte =
      parseInt(maxSizeLitteral) *
      (maxSizeLitteral.toLowerCase().includes('kb') ? 1000 : 1000000);
    if (fileSize > maxSizeInByte)
      return setCustomErrors({
        i18n: {
          title: i18nErrors.maxSize.title,
          body: `${i18nErrors.maxSize.body} ${maxSize}`,
        },
      });
    _URL.revokeObjectURL(objectUrl);
    return setValue(
      fieldName,
      [
        files.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        ),
      ].flat()
    );
  };
  img.src = objectUrl;
};

const handleMultipleDropAfterErrors = ({
  files,
  maxResolution,
  minResolution,
  fileExtension,
  maxSize,
  fieldName,
  i18nErrors,
  setValue,
  setCustomErrors,
  values = [],
  setAlt,
  setCaption,
  setPosition,
  setOldRevisionId,
}) => {
  var _URL = window.URL || window.webkitURL;
  Promise.all(
    files.map(
      (file) =>
        new Promise((resolve, reject) => {
          const img = new Image();
          var objectUrl = _URL.createObjectURL(file);
          img.onload = function () {
            const fileWidth = img.width;
            const fileHeight = img.height;
            const fileMaxSize = maxResolution.split('x');
            const maxWidth = parseInt(fileMaxSize?.[0]);
            const maxHeight = parseInt(fileMaxSize?.[1]);
            const fileMinSize = minResolution.split('x');
            const minWidth = parseInt(fileMinSize?.[0]);
            const minHeight = parseInt(fileMinSize?.[1]);
            const hasWidthError = fileWidth > maxWidth || fileWidth < minWidth;
            const hasHeightError =
              fileHeight > maxHeight || fileHeight < minHeight;
            if (hasWidthError || hasHeightError) {
              return setCustomErrors({
                i18n: {
                  title: i18nErrors.width.title,
                  body: `${i18nErrors.width.bodyBetween} ${minWidth}x${minHeight}px ${i18nErrors.width.bodyBetweenAnd} ${maxWidth}x${maxHeight}px`,
                },
              });
            }
            const fileFormat = file.path.split('.').slice(-1)[0];
            if (!fileExtension.includes(fileFormat))
              return setCustomErrors({
                i18n: {
                  title: i18nErrors.format.title,
                  body: `${i18nErrors.format.body} .${fileExtension}`,
                },
              });
            const fileSize = file.size;
            const maxSizeLitteral = maxSize;
            const maxSizeInByte =
              parseInt(maxSizeLitteral) *
              (maxSizeLitteral.toLowerCase().includes('kb') ? 1000 : 1000000);
            if (fileSize > maxSizeInByte)
              return setCustomErrors({
                i18n: {
                  title: i18nErrors.maxSize.title,
                  body: `${i18nErrors.maxSize.body} ${maxSize}`,
                },
              });
            _URL.revokeObjectURL(objectUrl);
            resolve(file);
          };
          img.onerror = reject;
          img.src = objectUrl;
        })
    )
  ).then((files) => {
    const images = values?.bp_image_field?.length ? values.bp_image_field : [];
    const newImagesValues = [
      ...images,
      ...files.map((file) => {
        setAlt((prev) => [...prev, '']);
        setOldRevisionId((prev) => [...prev, '']);
        setCaption((prev) => [...prev, '']);
        setPosition((prev) => [...prev, prev?.length]);
        return Object.assign(file, {
          preview: URL.createObjectURL(file),
        });
      }),
    ];

    return setValue(fieldName, newImagesValues);
  });
};

const parseUrl = (str) => (!str.includes('http') ? `https://${str}` : str);

const parseAdditionalContent = (content, productTitle) => {
  const c = content;
  if (c.name === ParagraphEnum.SD_BRAND_MULTITEXT) {
    // RichText - Text Block
    const val = c.field_paragraph_simple_text;
    return val
      ? {
          _tag: 'text-block',
          text: typeof val === 'string' ? JSON.parse(val) : val,
        }
      : undefined;
  } else if (c.name === ParagraphEnum.VIMEO_VIDEO) {
    // Video src - Vimeo Video
    const val = c.field_vimeo_link;
    const [videoNumber] = val.split('/').slice(-1);
    return val
      ? {
          _tag: 'embed-video',
          src: `https://player.vimeo.com/video/${videoNumber}`,
        }
      : undefined;
  } else if (c.name === ParagraphEnum.YOUTUBE_VIDEO) {
    // Video src - Youtube Video
    const videoSrc = c.field_url;
    const caption = c.field_didascalia
      ? typeof c.field_didascalia === 'string'
        ? JSON.parse(c.field_didascalia)
        : c.field_didascalia
      : undefined;
    return videoSrc
      ? {
          _tag: 'embed-video',
          src: videoSrc,
          ...(caption && { caption: caption }),
        }
      : undefined;
  } else if (c.name === ParagraphEnum.SDM_QUOTED_TEXT) {
    // Text Area - Quote Text
    const val = c.field_testo_semplice;
    return val
      ? {
          _tag: 'quote',
          text: val,
        }
      : undefined;
  } else if (c.name === ParagraphEnum.TITLE_SECTION) {
    // Text Area - Quote Text
    const val = c.field_section_title;
    return val
      ? {
          _tag: 'title-block',
          text: val,
        }
      : undefined;
  } else if (c.name === ParagraphEnum.FREE_IMAGE_FULL_SCREEN) {
    // Image - Full Screen
    const val = c.field_immagine_libera_full?.[0] || undefined;
    const src = val ? val?.preview || URL.createObjectURL(val) : undefined;
    const alt = val
      ? c?.alt_field_immagine_libera_full_0 || val.name
      : undefined;
    return src
      ? {
          _tag: 'full-width-image',
          src,
          alt,
        }
      : undefined;
  } else if (c.name === ParagraphEnum.DESIGNER) {
    // Textfield + Image
    const name = c.field_desiger_name || undefined;
    const img = c.field_image?.[0] || undefined;
    const src = img ? img?.preview || URL.createObjectURL(img) : undefined;
    const alt = img ? c?.alt_field_image_0 || img.name : undefined;
    return src || name
      ? {
          _tag: 'designer',
          name,
          src,
          alt,
        }
      : undefined;
  } else if (c.name === ParagraphEnum.FREE_IMAGE) {
    // Richtext + Image
    const caption = c.field_caption
      ? typeof c.field_caption === 'string'
        ? JSON.parse(c.field_caption)
        : c.field_caption
      : undefined;
    const img = c.field_immagine?.[0] || undefined;
    const src = img ? img?.preview || URL.createObjectURL(img) : undefined;
    const alt = img ? c?.alt_field_immagine_0 || img.name : undefined;
    return src
      ? {
          _tag: 'image-caption',
          src,
          alt,
          caption,
        }
      : undefined;
  } else if (c.name === ParagraphEnum.TECHNICAL_SHEET) {
    // Everything you can imagine
    const img = c.field_sheet_image?.[0] || undefined;
    const src = img ? img?.preview || URL.createObjectURL(img) : undefined;
    const alt = img ? c?.alt_field_sheet_image_0 || img.name : undefined;
    const overview = c.field_descrizione
      ? parseHtmlToDraw(`<p>${c.field_descrizione}</p>`)
      : '';
    const productName = productTitle || 'Product';
    const year = c.field_anno?.value || '';
    const designer =
      typeof c.field_designer === 'string'
        ? c.field_designer
        : c.field_designer?.label || '';
    const materials = c.field_materiali?.map((m) => m.label) || [];
    const features = c.field_caratteristiche?.map((m) => m.label) || [];
    const productionProcess = c.field_processo_produttivo
      ? parseHtmlToDraw(`<p>${c.field_processo_produttivo}</p>`)
      : '';
    const dimensions = c.field_dimensioni
      ? parseHtmlToDraw(`<p>${c.field_dimensioni}</p>`)
      : '';
    const officialWebsiteLink = {
      url: c.field_website ? parseUrl(c.field_website) : '',
      text: c.alt_field_website_0 || '',
    };
    const productSheetLink = {
      url: c.field_modello_3d ? parseUrl(c.field_modello_3d) : '',
      text: c.alt_field_modello_3d_0 || '',
    };
    const collectionLink = {
      url: c.field_collezione ? parseUrl(c.field_collezione) : '',
      text: c.alt_field_collezione_0 || '',
    };
    return {
      _tag: 'data-sheet',
      ...(src && {
        src: src,
      }),
      ...(alt && {
        alt: alt,
      }),
      overview,
      productName,
      year,
      designer,
      materials,
      features,
      productionProcess,
      dimensions,
      officialWebsiteLink,
      productSheetLink,
      collectionLink,
    };
  } else if (c.name === ParagraphEnum.SLIDER_DETAIL) {
    // Array of Images and RichText - Slider
    const val = c?.bp_image_field || [];
    const images = val.map((v, idx) => {
      const img = v || undefined;
      const src = img ? img?.preview || URL.createObjectURL(img) : undefined;
      const caption = c.fileCaptions?.[idx]
        ? typeof c.fileCaptions?.[idx] === 'string'
          ? JSON.parse(c.fileCaptions?.[idx])
          : c.fileCaptions?.[idx]
        : undefined;
      const alt = img ? c.fileAlts?.[idx] || img.name : undefined;
      const position = img ? c.filePositions?.[idx] : undefined;
      return {
        src,
        alt,
        caption,
        position,
      };
    });

    return {
      _tag: 'image-slider',
      images,
    };
  } else if (c.name === ParagraphEnum.VIRTUAL_SHOWROOM) {
    // Textfields and an image
    const src = c.field_link_vs || undefined;
    const title = c.field_titolo_vs || undefined;
    return src
      ? {
          _tag: 'showroom',
          title,
          src,
        }
      : undefined;
  }
};

const parseDataToPreview = (data) => {
  const contents = data?.additionalContents
    ?.sort((a, b) => a.position - b.position)
    ?.map((c) => parseAdditionalContent(c, data.title))
    .filter((v) => !!v);

  const socialLinks = [];

  if (data.field_link_facebook)
    socialLinks.push({ social: 'facebook', link: data.field_link_facebook });
  if (data.field_link_instagram)
    socialLinks.push({ social: 'instagram', link: data.field_link_instagram });
  if (data.field_link_linkedin)
    socialLinks.push({ social: 'linkedin', link: data.field_link_linkedin });
  if (data.field_link_pinterest)
    socialLinks.push({ social: 'pinterest', link: data.field_link_pinterest });
  if (data.field_link_twitter)
    socialLinks.push({ social: 'twitter', link: data.field_link_twitter });

  const parsedData = {
    ...(data.title && {
      name: data.title,
    }),
    ...(data.field_cover?.length && {
      cover: {
        img:
          data.field_cover[0]?.preview ||
          URL.createObjectURL(data.field_cover?.[0]),
        alt: data?.alt_field_cover_0 || data.field_cover[0].name,
      },
    }),
    ...(data.body && {
      overview:
        typeof data.body === 'string' ? JSON.parse(data.body) : data.body,
    }),
    ...(contents?.length && {
      contents: contents,
    }),
    ...(data.field_classe_merceologica && {
      category: data.field_classe_merceologica?.label,
    }),
    ...(data.field_private_collezione && {
      collection:
        data.field_private_collezione?.label || data.field_private_collezione,
    }),
    ...(data.field_didascalia && {
      heroText:
        typeof data.field_didascalia === 'string'
          ? JSON.parse(data.field_didascalia)
          : data.field_didascalia,
    }),
    socialLinks,
  };
  return parsedData;
};

export {
  parseEntity,
  parseEntityWithForm,
  parseEntityWithReactFC,
  handleDropAfterErrors,
  handleMultipleDropAfterErrors,
  parseFetchedEntity,
  parseDataToPreview,
  parseHtmlToDraw,
};
